import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ComposedChart,
  Line,
  Area,
  Bar,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
  CartesianGrid,
  Scatter,
} from 'recharts';
import { useLogin } from '../contexts/LoginContext';
import { getCurrentUser } from '../utils/currentUser';
import { useSignalWatches, saveSignalWatch, removeSignalWatch, fetchHistoricalPricesBatch } from '../services/api';
import { STRATEGIES, STRATEGY_LABELS, DEFAULT_STRATEGY } from '../constants/signalStrategies';
import { sigmaForPeriod, computeBandsSeries, isShortStrategy, resolveChartFit, FIT_HISTORY_DAYS } from '../shared/bandSignals';
import { stopKOfWatch } from '../utils/strategyTabs';
import { SMA_PERIODS, RANGES, AUTO_STRATEGY, loadChartSettings, saveChartSettings, rangeDaysFor, rangeLabelFor } from '../utils/bollingerChartSettings';

// Backtest trade markers: ▲ buy below the point, ▼ sell above it
const TradeMarker = ({ cx, cy, direction }) => {
  if (cx == null || cy == null) return null;
  const s = 5;
  const offset = 11;
  const y = direction === 'up' ? cy + offset : cy - offset;
  const points = direction === 'up'
    ? `${cx},${y - s} ${cx - s},${y + s} ${cx + s},${y + s}`
    : `${cx},${y + s} ${cx - s},${y - s} ${cx + s},${y - s}`;
  const fill = direction === 'up' ? '#16a34a' : '#dc2626';
  return <polygon points={points} fill={fill} stroke="white" strokeWidth={1} />;
};

// Mean targets are FROZEN at entry (the entry bar's SMA): a moving SMA chases
// price, which makes fast bands' reversion trivially easy to "win". STRATEGIES
// and DEFAULT_STRATEGY are shared with the notifications UI via constants;
// isShortStrategy/usesBandExit classify keys in src/shared/bandSignals.js;
// SMA_PERIODS/RANGES/AUTO_STRATEGY and the per-stock localStorage settings
// live in utils/bollingerChartSettings.js, shared with useWatchBacktests so
// the notifications dialog scores each watch over this chart's own range.

// Identity of a saved fit, for "has the chart already shown this fit" checks
const watchFitKey = (ticker, strategy, period, lookbackDays) => `${ticker}|${strategy}|${period}|${lookbackDays}`;

const fetchHistoricalPrices = (symbol) =>
  fetchHistoricalPricesBatch([symbol], FIT_HISTORY_DAYS).then((bySymbol) => bySymbol[symbol] || []);

// Chart-only decoration over the shared band math: up/down-day coloring and
// the [lower, upper] tuple Recharts' Area needs.
const computeBands = (prices, period) =>
  computeBandsSeries(prices, period).map((row, i, arr) => ({
    ...row,
    upDay: i > 0 ? row.close >= arr[i - 1].close : true,
    band: row.upper == null ? undefined : [row.lower, row.upper],
  }));

// Fit scoring (score = cumulative trade return of the one-position backtest,
// MIN_FIT_TRADES round trips to rank) lives in src/shared/bandSignals.js as
// fitStatsFor/autoFit — the SAME ranking signal-watch-cron's periodic refit
// runs (issue #472), so the server never picks a fit the chart wouldn't. The
// returned spans make frozen mean targets visible (a memory of the SMA, not
// the drawn line). In 'band' an upper-band touch is an EXIT, never a short —
// scoring it as a reversion short made every uptrending stock look negative.

const formatDateTick = (date) => {
  const d = new Date(`${date}T00:00:00`);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// Earnings dates arrive as ISO timestamps (e.g. 2025-01-30T20:00:00.000Z);
// the UTC calendar day is the report date and matches the chart's YYYY-MM-DD keys
const toEarningsDay = (d) => (d ? new Date(d).toISOString().slice(0, 10) : null);
const formatEarnDate = (day) => (day ? formatDateTick(day) : null);

// How far the projected-band / expected-move extension runs past the last bar
const PROJECT_DAYS = 5;

// The next N weekday (Mon–Fri) YYYY-MM-DD strings after a date. Noon-UTC anchor
// so slice(0,10) is DST/timezone-safe and lines up with the chart's date keys.
const nextWeekdays = (isoDay, n) => {
  const out = [];
  const d = new Date(`${isoDay}T12:00:00Z`);
  while (out.length < n) {
    d.setUTCDate(d.getUTCDate() + 1);
    const dow = d.getUTCDay();
    if (dow !== 0 && dow !== 6) out.push(d.toISOString().slice(0, 10));
  }
  return out;
};

// Std-dev of daily simple returns over the last `window` closes — the realized
// vol that scales the expected-move cone (±1σ·√days from the last close).
const dailyReturnStd = (closes, window = 20) => {
  const tail = closes.slice(-(window + 1));
  if (tail.length < 3) return 0;
  const rets = [];
  for (let i = 1; i < tail.length; i++) rets.push(tail[i] / tail[i - 1] - 1);
  const mean = rets.reduce((a, b) => a + b, 0) / rets.length;
  const variance = rets.reduce((a, b) => a + (b - mean) ** 2, 0) / rets.length;
  return Math.sqrt(variance);
};

export const formatVolume = (num) => {
  if (num >= 1e9) return (num / 1e9).toFixed(1).replace(/\.0$/, '') + 'B';
  if (num >= 1e6) return (num / 1e6).toFixed(1).replace(/\.0$/, '') + 'M';
  if (num >= 1e3) return (num / 1e3).toFixed(1).replace(/\.0$/, '') + 'k';
  return String(num);
};

const pillStyle = (active) => ({
  padding: '2px 10px',
  fontSize: '12px',
  borderRadius: '9999px',
  border: `1px solid ${active ? '#3b82f6' : '#d1d5db'}`,
  backgroundColor: active ? '#3b82f6' : 'white',
  color: active ? 'white' : '#374151',
  cursor: 'pointer',
});

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const point = payload[0]?.payload;
  if (!point) return null;
  // Future/projected rows carry no close — show the projected bands + cone instead
  const isProjected = point.close == null && point.projUpper != null;
  const rows = (isProjected
    ? [
        { name: 'Proj. Upper', value: point.projUpper?.toFixed(2), color: '#6b7280' },
        { name: 'Proj. SMA', value: point.projSma?.toFixed(2), color: '#f59e0b' },
        { name: 'Proj. Lower', value: point.projLower?.toFixed(2), color: '#6b7280' },
        { name: 'Exp. move (±1σ)', value: point.cone ? `${point.cone[0].toFixed(2)}–${point.cone[1].toFixed(2)}` : null, color: '#6b7280' },
      ]
    : [
        { name: '▲ Strategy buy', value: point.buyPoint?.toFixed(2), color: '#16a34a' },
        { name: '▼ Strategy sell', value: point.sellPoint?.toFixed(2), color: '#dc2626' },
        { name: 'Trade target', value: point.tradeTarget?.toFixed(2), color: '#8b5cf6' },
        { name: 'Close', value: point.close?.toFixed(2), color: '#3b82f6' },
        { name: 'SMA', value: point.sma?.toFixed(2), color: '#f59e0b' },
        { name: 'Upper Band', value: point.upper?.toFixed(2), color: '#6b7280' },
        { name: 'Lower Band', value: point.lower?.toFixed(2), color: '#6b7280' },
        { name: 'Volume', value: point.volume != null ? formatVolume(point.volume) : null, color: '#6b7280' },
      ]
  ).filter((r) => r.value != null);
  return (
    <div style={{
      backgroundColor: 'white',
      border: '1px solid #e5e7eb',
      borderRadius: '6px',
      padding: '8px 10px',
      fontSize: '12px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    }}>
      <div style={{ fontWeight: 600, marginBottom: '4px' }}>
        {formatDateTick(label)}{isProjected ? ' · projected' : ''}
      </div>
      {rows.map((r) => (
        <div key={r.name} style={{ color: r.color }}>
          {r.name}: {r.value}
        </div>
      ))}
    </div>
  );
};

const PriceBollingerChart = ({ symbol, fiftyTwoWeekLow, fiftyTwoWeekHigh, lastEarningsDate, nextEarningsDate, nextEarningsEstimated, lastEarningsGapPct }) => {
  const [settings, setSettings] = useState(() => loadChartSettings(symbol));
  const { sma: smaPeriod, range, auto: autoFit, strategy } = settings;

  // Which watched fit this chart instance has already opened on (see the
  // watched-fit effect below). Reset per stock, so leaving a watched stock and
  // coming back re-applies its fit instead of the local settings.
  const appliedWatchFitRef = useRef(null);

  // Re-load saved settings if the same mounted chart switches stocks
  useEffect(() => {
    appliedWatchFitRef.current = null;
    setSettings(loadChartSettings(symbol));
  }, [symbol]);

  const updateSettings = (overrides) => {
    setSettings((prev) => {
      const next = { ...prev, ...overrides };
      saveChartSettings(symbol, next);
      return next;
    });
  };

  const { data: prices, isLoading, isError } = useQuery({
    queryKey: ['historicalPrices', symbol],
    queryFn: () => fetchHistoricalPrices(symbol),
    enabled: !!symbol,
    staleTime: 60 * 60 * 1000, // daily bars; no need to refetch within the hour
    gcTime: 60 * 60 * 1000,
  });

  const rangeDays = rangeDaysFor(range);

  // ---- Signal-watch state (also read by the fit: the strategy's stop loss) ----
  const { triggerLogin } = useLogin();
  const { isLoggedIn, email } = getCurrentUser();
  const queryClient = useQueryClient();
  const { data: watchData } = useSignalWatches(isLoggedIn, email);
  const upperSymbol = (symbol || '').toUpperCase();
  const watched = watchData?.watches?.find((w) => w.ticker === upperSymbol) || null;

  // A watched stock opens on the WATCHED fit — shape, period and range — so the
  // chart shows what the scans trade, including a fit the morning refit chose
  // (issue #472). Without this, a chart pinned to an older fit offered "Update
  // watch" and one click silently undid the refit. Applied once per distinct
  // watched fit (not persisted), so Auto stays one click away to compare and a
  // background refetch never yanks the view back. A fit the pills can't show
  // (legacy 100D, an unknown range) leaves the chart's own settings alone.
  const watchedRange = watched ? rangeLabelFor(watched.lookback_days) : null;
  const watchedFitKey = watched && watchedRange && SMA_PERIODS.includes(watched.period)
    ? watchFitKey(upperSymbol, watched.strategy, watched.period, watched.lookback_days) : null;
  useEffect(() => {
    if (!watchedFitKey || appliedWatchFitRef.current === watchedFitKey) return;
    appliedWatchFitRef.current = watchedFitKey;
    setSettings((prev) => ({ ...prev, strategy: watched.strategy, sma: watched.period, auto: false, range: watchedRange }));
  }, [watchedFitKey]); // eslint-disable-line react-hooks/exhaustive-deps
  // Backtest under the stop of the strategy this stock is (or would be) watched
  // in — issue #417. Signed out / no strategy yet → the fitted default.
  const stopK = stopKOfWatch(watchData?.strategies, watched);

  // On 'auto', every shape is backtested and the winner's fit is shown. With
  // period Auto on, each shape competes at its own best period; with a manual
  // period, shapes compete at that period. resolvedStrategy is what everything
  // downstream (markers, summary, saved watches) actually uses.
  const { stats: fitStats, best: bestFitPeriod, resolvedStrategy } = useMemo(() => {
    if (!prices?.length) {
      return { stats: {}, best: null, resolvedStrategy: strategy === AUTO_STRATEGY ? DEFAULT_STRATEGY : strategy };
    }
    return resolveChartFit(prices, rangeDays, {
      auto: strategy === AUTO_STRATEGY, autoPeriod: autoFit, strategy, smaPeriod, stopK, defaultStrategy: DEFAULT_STRATEGY,
    });
  }, [prices, rangeDays, strategy, autoFit, smaPeriod, stopK]);

  const effectivePeriod = autoFit && bestFitPeriod != null ? bestFitPeriod : smaPeriod;
  const effectiveSigma = sigmaForPeriod(effectivePeriod);

  const selectSmaPeriod = (period) => updateSettings({ sma: period, auto: false });
  const selectRange = (label) => updateSettings({ range: label });
  // The Auto pill governs both dimensions: lit while either the period or the
  // shape is auto-fitting. Turning it off freezes the shape at the current
  // winner — the 'auto' slot holds no last-manual shape to fall back to
  // (period falls back to the last manual pick, as before). Picking a shape
  // from the dropdown pins just the shape; the period keeps auto-fitting.
  const anyAuto = autoFit || strategy === AUTO_STRATEGY;
  const toggleAutoFit = () => updateSettings(anyAuto
    ? { auto: false, strategy: resolvedStrategy }
    : { auto: true, strategy: AUTO_STRATEGY });
  const selectStrategy = (key) => updateSettings({ strategy: key });

  // ---- Signal-watch toggle: persist the current fit server-side for alerts ----
  const watchCount = watchData?.count ?? 0;
  const watchMax = watchData?.max ?? 10;
  // Whether the watched record matches the fit currently on screen
  // The range is part of the fit (issue #339): the same shape confirmed over a
  // different window is a different fit as far as "has this fit's window turned
  // over" is concerned, so it must be re-saveable. Rows predating the column
  // carry the backfilled default and compare like any other.
  const watchedSameFit = watched && watched.period === effectivePeriod && watched.strategy === resolvedStrategy
    && watched.lookback_days === rangeDays;
  const [watchBusy, setWatchBusy] = useState(false);
  const [watchError, setWatchError] = useState(null);

  const runWatchMutation = async (fn) => {
    setWatchError(null);
    setWatchBusy(true);
    try {
      await fn();
      queryClient.invalidateQueries(['signalWatches']);
    } catch (e) {
      setWatchError(e.code === 'CAP_REACHED' ? `Limit ${watchMax} reached — remove one first` : 'Could not save watch');
    } finally {
      setWatchBusy(false);
    }
  };

  const saveCurrentFit = () => {
    // The fit being saved is already on screen: mark it applied so the refetch
    // doesn't switch the view off Auto
    appliedWatchFitRef.current = watchFitKey(upperSymbol, resolvedStrategy, effectivePeriod, rangeDays);
    return runWatchMutation(() => saveSignalWatch({ ticker: upperSymbol, period: effectivePeriod, strategy: resolvedStrategy, lookbackDays: rangeDays }));
  };

  const handleWatchToggle = () => {
    if (!isLoggedIn) { triggerLogin(); return; }
    if (watched) runWatchMutation(() => removeSignalWatch(upperSymbol));
    else saveCurrentFit();
  };

  const handleWatchUpdate = saveCurrentFit;

  const chartData = useMemo(() => {
    if (!prices?.length) return [];
    const rows = computeBands(prices, effectivePeriod).slice(-rangeDays);
    // Stamp the shown combo's backtest entries/exits and frozen targets
    const events = fitStats[effectivePeriod]?.events;
    if (!events?.length) return rows;
    const byDate = new Map(events.map((e) => [e.date, e.type]));
    const spans = fitStats[effectivePeriod]?.spans;
    return rows.map((r) => {
      const type = byDate.get(r.date);
      // ISO dates compare lexicographically
      const span = spans?.find((s) => r.date >= s.from && r.date <= s.to);
      if (!type && !span) return r;
      const out = { ...r };
      if (type) out[type === 'buy' ? 'buyPoint' : 'sellPoint'] = r.close;
      if (span) out.tradeTarget = span.target;
      return out;
    });
  }, [prices, effectivePeriod, rangeDays, fitStats]);

  // Project ~1 week past the last bar: bands assuming price holds flat at the
  // last close (so you see where the bands drift), plus a ±1σ·√days expected-
  // move cone from realized vol. Flat-hold reuses computeBandsSeries verbatim —
  // append flat future closes, recompute, take the tail — so the projected
  // bands can't diverge from the solid ones. No directional price call is made.
  const projection = useMemo(() => {
    if (!prices?.length || !chartData.length) return [];
    const period = effectivePeriod;
    const closes = prices.map((p) => p.close);
    const lastClose = closes[closes.length - 1];
    const lastDay = chartData[chartData.length - 1].date;
    const futureDays = nextWeekdays(lastDay, PROJECT_DAYS);
    // period-1 real closes warm up the rolling window; flat future closes follow
    const seed = closes.slice(-(period - 1)).map((close) => ({ close }));
    const future = futureDays.map((date) => ({ date, close: lastClose }));
    const projected = computeBandsSeries([...seed, ...future], period).slice(seed.length);
    const ds = dailyReturnStd(closes);
    return projected.map((b, i) => {
      const move = lastClose * ds * Math.sqrt(i + 1);
      return {
        date: b.date,
        projSma: b.sma,
        projUpper: b.upper,
        projLower: b.lower,
        cone: [lastClose - move, lastClose + move],
      };
    });
  }, [prices, chartData, effectivePeriod]);

  // Real bars + projection. The last real bar is stamped with proj*/cone anchors
  // (its own band values, zero-width cone) so the dashed projected lines and the
  // cone connect seamlessly out of the solid history instead of floating.
  const displayData = useMemo(() => {
    if (!projection.length) return chartData;
    const stampedReal = chartData.map((r, i) =>
      i === chartData.length - 1
        ? { ...r, projSma: r.sma, projUpper: r.upper, projLower: r.lower, cone: [r.close, r.close] }
        : r
    );
    return [...stampedReal, ...projection];
  }, [chartData, projection]);

  const low52 = Number.isFinite(fiftyTwoWeekLow) ? fiftyTwoWeekLow : null;
  const high52 = Number.isFinite(fiftyTwoWeekHigh) ? fiftyTwoWeekHigh : null;

  // Domain follows the chart data. A 52W line is drawn only if it sits near the
  // visible range — a far-away extreme (e.g. post-IPO high 10x above current
  // price) would otherwise squash the price action into a sliver of the plot.
  // Off-scale extremes are reported as a text note instead.
  const { yDomain, maxVolume, show52High, show52Low } = useMemo(() => {
    if (!displayData.length) return { yDomain: ['auto', 'auto'], maxVolume: 0, show52High: false, show52Low: false };
    let min = Infinity;
    let max = -Infinity;
    let maxVol = 0;
    for (const p of displayData) {
      // Include projected bands + expected-move cone so the extension isn't clipped
      min = Math.min(min, p.lower ?? p.close ?? Infinity, p.low ?? p.close ?? Infinity, p.projLower ?? Infinity, p.cone?.[0] ?? Infinity);
      max = Math.max(max, p.upper ?? p.close ?? -Infinity, p.high ?? p.close ?? -Infinity, p.projUpper ?? -Infinity, p.cone?.[1] ?? -Infinity);
      if (p.volume > maxVol) maxVol = p.volume;
    }
    const maxExpand = (max - min) * 0.35;
    const show52High = high52 != null && high52 <= max + maxExpand;
    const show52Low = low52 != null && low52 >= min - maxExpand;
    if (show52High) max = Math.max(max, high52);
    if (show52Low) min = Math.min(min, low52);
    const pad = (max - min) * 0.03;
    return { yDomain: [min - pad, max + pad], maxVolume: maxVol, show52High, show52Low };
  }, [displayData, low52, high52]);

  // Earnings markers over the display window (real bars + projection). The last
  // report snaps to the first trading day on/after it; the next report is
  // usually right of the chart (caption-only), but if it lands inside the
  // projected week it gets its own on-chart marker too.
  const earnings = useMemo(() => {
    if (!displayData.length) return { markerDate: null, nextMarkerDate: null, next: null };
    const firstDay = displayData[0].date;
    const lastDay = displayData[displayData.length - 1].date;
    const snap = (iso) => {
      const day = toEarningsDay(iso);
      return day && day >= firstDay && day <= lastDay ? displayData.find((r) => r.date >= day)?.date || null : null;
    };
    let next = null;
    const ne = toEarningsDay(nextEarningsDate);
    if (ne) {
      const days = Math.ceil((new Date(`${ne}T00:00:00`) - new Date()) / 86400000);
      next = { day: ne, days: days > 0 ? days : null };
    }
    return { markerDate: snap(lastEarningsDate), nextMarkerDate: snap(nextEarningsDate), next };
  }, [displayData, lastEarningsDate, nextEarningsDate]);

  const gapColor = lastEarningsGapPct == null ? '#6b7280' : lastEarningsGapPct >= 0 ? '#16a34a' : '#dc2626';

  if (!symbol) return null;

  if (isLoading) {
    return (
      <div style={{ height: '280px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: '13px' }}>
        Loading price history…
      </div>
    );
  }

  if (isError || !chartData.length) {
    return (
      <div style={{ height: '60px', display: 'flex', alignItems: 'center', color: '#9ca3af', fontSize: '13px' }}>
        Price history unavailable for {symbol}.
      </div>
    );
  }

  const refLabelStyle = { fontSize: 10, fill: '#6b7280' };

  // Notes for 52W extremes too far outside the visible range to draw,
  // overlaid in the chart corners where the reference-line labels would sit
  const lastClose = chartData[chartData.length - 1]?.close;
  const offScaleHigh = high52 != null && !show52High && lastClose
    ? `52W High ${high52.toFixed(2)} ↑ (+${((high52 / lastClose - 1) * 100).toFixed(0)}% vs close)`
    : null;
  const offScaleLow = low52 != null && !show52Low && lastClose
    ? `52W Low ${low52.toFixed(2)} ↓ (−${((1 - low52 / lastClose) * 100).toFixed(0)}% vs close)`
    : null;
  const cornerNoteStyle = {
    position: 'absolute',
    right: '8px',
    fontSize: '10px',
    color: '#6b7280',
    backgroundColor: 'rgba(255,255,255,0.75)',
    padding: '1px 4px',
    borderRadius: '3px',
    pointerEvents: 'none',
  };

  // Backtest result of the shown period over the visible window. Cumulative
  // across trades, so it can exceed ±100%; color makes the sign unmissable
  // (negative = buying this band's dips lost money on this stock/window).
  const isShort = isShortStrategy(resolvedStrategy);
  const fit = fitStats[effectivePeriod];
  let fitNote = null;
  if (fit && fit.trades > 0) {
    fitNote = (
      <>
        <span style={{ color: fit.totalReturn >= 0 ? '#16a34a' : '#dc2626', fontWeight: 600 }}>
          {fit.totalReturn >= 0 ? '+' : '−'}{Math.abs(fit.totalReturn * 100).toFixed(0)}%
        </span>
        {` · ${fit.wins}/${fit.trades} trades won`}
      </>
    );
  } else if (fit) {
    fitNote = `no ${isShort ? 'upper' : 'lower'}-band entries in this window`;
  }

  // Shown period fell below MIN_FIT_TRADES: flag it on the Auto pill (red) instead
  // of spending horizontal space on a text note
  const autoWarning = !!fit && !fit.eligible;
  const autoPillStyle = autoWarning
    ? {
        ...pillStyle(anyAuto),
        border: '1px solid #dc2626',
        backgroundColor: anyAuto ? '#dc2626' : 'white',
        color: anyAuto ? 'white' : '#dc2626',
      }
    : pillStyle(anyAuto);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '12px', color: '#6b7280' }}>SMA:</span>
        <button
          onClick={toggleAutoFit}
          title={autoWarning
            ? 'Too few trades to auto-rank — needs 2+ completed trades in this window. Try a longer range.'
            : 'Pick the period and strategy shape that traded best in this window. Picking a period or shape by hand pins that dimension; Auto keeps fitting the rest.'}
          style={autoPillStyle}
        >
          Auto
        </button>
        {SMA_PERIODS.map((period) => (
          <button key={period} onClick={() => selectSmaPeriod(period)} style={pillStyle(effectivePeriod === period)}>
            {period}D
          </button>
        ))}
        <span
          title="Band width scales with the SMA period: ±1σ for 3-5D, ±1.5σ for 10-20D, ±2σ for 50D+"
          style={{ fontSize: '11px', color: '#9ca3af', marginRight: '4px' }}
        >
          ±{effectiveSigma}σ
        </span>
        {/* The select's face always shows the RESOLVED shape (auto's winner or
            the manual pick), styled blue like the other active selections.
            Choosing an option pins that shape; the Auto pill re-arms it. */}
        <select
          value={resolvedStrategy}
          onChange={(e) => selectStrategy(e.target.value)}
          title={strategy === AUTO_STRATEGY
            ? `Auto-picked: ${STRATEGY_LABELS[resolvedStrategy]} traded best in this window. Picking a shape here pins it.`
            : STRATEGIES.find((s) => s.key === resolvedStrategy)?.hint}
          style={{
            fontSize: '12px',
            padding: '2px 4px',
            borderRadius: '6px',
            border: '1px solid #3b82f6',
            backgroundColor: '#3b82f6',
            color: 'white',
            cursor: 'pointer',
            marginRight: '4px',
          }}
        >
          {STRATEGIES.map((s) => (
            <option key={s.key} value={s.key} style={{ backgroundColor: 'white', color: '#374151' }}>
              {s.label}
            </option>
          ))}
        </select>
        {RANGES.map((r) => (
          <button key={r.label} onClick={() => selectRange(r.label)} style={pillStyle(range === r.label)}>
            {r.label}
          </button>
        ))}
        {fitNote && (
          <span
            title={stopK ? `Backtest includes a ${stopK}σ stop loss — your strategy's setting, on its strategy card` : 'Backtest runs with no stop loss — your strategy\'s stop is off (strategy card)'}
            style={{ fontSize: '11px', color: '#9ca3af' }}
          >
            {fitNote}
          </span>
        )}
        <span style={{ flex: 1 }} />
        <button
          onClick={handleWatchToggle}
          disabled={watchBusy}
          title={
            !isLoggedIn
              ? `Sign in to get alerts when ${upperSymbol} triggers a signal`
              : watched
              ? `Watching ${upperSymbol} (${watched.strategy}/${watched.period}D) — click to stop. ${watchCount}/${watchMax} used.`
              : `Alert me when ${upperSymbol} triggers a ${resolvedStrategy} signal at the current fit (${effectivePeriod}D). ${watchCount}/${watchMax} used.`
          }
          style={{
            ...pillStyle(!!watched),
            padding: '2px 7px',
            border: `1px solid ${watched ? '#8b5cf6' : '#d1d5db'}`,
            backgroundColor: watched ? '#ede9fe' : 'white',
            opacity: watchBusy ? 0.5 : 1,
            // dim the bell when not watching, full-strength (purple tint) when watching
            filter: watched ? 'none' : 'grayscale(1) opacity(0.55)',
            lineHeight: 1,
          }}
        >
          🔔
        </button>
        {watched && !watchedSameFit && (
          <button
            onClick={handleWatchUpdate}
            disabled={watchBusy}
            title={`Update the alert to the fit currently shown (${resolvedStrategy}/${effectivePeriod}D over ${range})`}
            style={{ background: 'none', border: 'none', color: '#8b5cf6', fontSize: '11px', cursor: 'pointer', textDecoration: 'underline', padding: 0 }}
          >
            update fit
          </button>
        )}
        {watchError && <span style={{ fontSize: '11px', color: '#dc2626' }}>{watchError}</span>}
      </div>
      <div style={{ position: 'relative' }}>
        {fit?.trades > 0 && (
          // left offset clears the 45px Y-axis + chart margin
          <div style={{ ...cornerNoteStyle, right: 'auto', left: '52px', top: '6px' }}>
            {isShort ? (
              <><span style={{ color: '#dc2626' }}>▼ short</span> <span style={{ color: '#16a34a' }}>▲ cover</span></>
            ) : (
              <><span style={{ color: '#16a34a' }}>▲ buy</span> <span style={{ color: '#dc2626' }}>▼ sell</span></>
            )}
          </div>
        )}
        {offScaleHigh && <div style={{ ...cornerNoteStyle, top: '6px' }}>{offScaleHigh}</div>}
        {offScaleLow && <div style={{ ...cornerNoteStyle, bottom: '34px' }}>{offScaleLow}</div>}
      <ResponsiveContainer width="100%" height={280}>
        <ComposedChart data={displayData} margin={{ top: 5, right: 5, bottom: 0, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
          <XAxis
            dataKey="date"
            tickFormatter={formatDateTick}
            tick={{ fontSize: 11, fill: '#6b7280' }}
            minTickGap={40}
          />
          <YAxis
            domain={yDomain}
            tick={{ fontSize: 11, fill: '#6b7280' }}
            tickFormatter={(v) => v.toFixed(0)}
            width={45}
            allowDataOverflow
          />
          {/* Hidden axis scales volume bars into the bottom ~20% of the plot */}
          <YAxis yAxisId="vol" hide domain={[0, maxVolume * 5]} />
          <Tooltip content={<ChartTooltip />} />
          <Bar yAxisId="vol" dataKey="volume" isAnimationActive={false}>
            {displayData.map((p) => (
              <Cell key={p.date} fill={p.upDay ? 'rgba(34,197,94,0.35)' : 'rgba(239,68,68,0.35)'} />
            ))}
          </Bar>
          {/* Expected-move cone (±1σ·√days from last close) over the projected
              week — grey = uncertainty, drawn behind the bands/price */}
          <Area
            dataKey="cone"
            stroke="none"
            fill="#6b7280"
            fillOpacity={0.14}
            connectNulls
            isAnimationActive={false}
          />
          <Area
            dataKey="band"
            stroke="none"
            fill="#3b82f6"
            fillOpacity={0.08}
            connectNulls
            isAnimationActive={false}
          />
          <Line
            dataKey="upper"
            stroke="#9ca3af"
            strokeWidth={1}
            strokeDasharray="4 3"
            dot={false}
            connectNulls
            isAnimationActive={false}
          />
          <Line
            dataKey="lower"
            stroke="#9ca3af"
            strokeWidth={1}
            strokeDasharray="4 3"
            dot={false}
            connectNulls
            isAnimationActive={false}
          />
          <Line
            dataKey="sma"
            stroke="#f59e0b"
            strokeWidth={1.5}
            dot={false}
            connectNulls
            isAnimationActive={false}
          />
          {/* Frozen exit target per trade (mean strategies); no connectNulls
              so each trade renders as its own horizontal segment */}
          <Line
            dataKey="tradeTarget"
            stroke="#8b5cf6"
            strokeWidth={1}
            strokeDasharray="2 3"
            dot={false}
            isAnimationActive={false}
          />
          <Line
            dataKey="close"
            stroke="#3b82f6"
            strokeWidth={2}
            dot={false}
            isAnimationActive={false}
          />
          {/* Projected bands (flat-hold): dotted continuations of upper/lower/SMA
              past the last real bar. connectNulls bridges the single anchor on
              the last real bar into the future rows. */}
          <Line dataKey="projUpper" stroke="#9ca3af" strokeWidth={1} strokeDasharray="1 3" dot={false} connectNulls isAnimationActive={false} />
          <Line dataKey="projLower" stroke="#9ca3af" strokeWidth={1} strokeDasharray="1 3" dot={false} connectNulls isAnimationActive={false} />
          <Line dataKey="projSma" stroke="#f59e0b" strokeWidth={1} strokeDasharray="1 3" dot={false} connectNulls isAnimationActive={false} />
          <Scatter
            dataKey="buyPoint"
            shape={(props) => <TradeMarker {...props} direction="up" />}
            isAnimationActive={false}
          />
          <Scatter
            dataKey="sellPoint"
            shape={(props) => <TradeMarker {...props} direction="down" />}
            isAnimationActive={false}
          />
          {/* Divider between real history and the projected week */}
          {projection.length > 0 && (
            <ReferenceLine
              x={chartData[chartData.length - 1].date}
              stroke="#d1d5db"
              strokeDasharray="3 3"
              strokeWidth={1}
              label={{ value: 'now', position: 'insideTopRight', fontSize: 9, fill: '#9ca3af' }}
            />
          )}
          {[...new Set([earnings.markerDate, earnings.nextMarkerDate].filter(Boolean))].map((d) => (
            <ReferenceLine
              key={d}
              x={d}
              stroke="#6b7280"
              strokeDasharray="2 2"
              strokeWidth={1}
              label={{ value: 'E', position: 'insideTop', fontSize: 10, fontWeight: 700, fill: '#4b5563' }}
            />
          ))}
          {show52High && (
            <ReferenceLine
              y={high52}
              stroke="#9ca3af"
              strokeDasharray="6 4"
              label={{ value: `52W High ${high52.toFixed(2)}`, position: 'insideTopRight', ...refLabelStyle }}
            />
          )}
          {show52Low && (
            <ReferenceLine
              y={low52}
              stroke="#9ca3af"
              strokeDasharray="6 4"
              label={{ value: `52W Low ${low52.toFixed(2)}`, position: 'insideBottomRight', ...refLabelStyle }}
            />
          )}
        </ComposedChart>
      </ResponsiveContainer>
      </div>
      {(lastEarningsDate || earnings.next || projection.length > 0) && (
        <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '6px', display: 'flex', gap: '14px', flexWrap: 'wrap' }}>
          {lastEarningsDate && (
            <span>
              <span style={{ color: '#9ca3af' }}>E</span> Last earnings {formatEarnDate(toEarningsDay(lastEarningsDate))}
              {lastEarningsGapPct != null && (
                <b style={{ color: gapColor, marginLeft: '4px' }}>
                  {lastEarningsGapPct >= 0 ? '▲' : '▼'}{Math.abs(lastEarningsGapPct).toFixed(1)}%
                </b>
              )}
            </span>
          )}
          {earnings.next && (
            <span>
              Next earnings {formatEarnDate(earnings.next.day)}
              {nextEarningsEstimated ? ' (est.)' : ''}
              {earnings.next.days ? ` · in ${earnings.next.days}d` : ''}
            </span>
          )}
          {projection.length > 0 && (
            <span style={{ color: '#9ca3af' }}>
              Dotted = projected bands (flat) · grey cone = ±1σ expected move, {PROJECT_DAYS}d
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default PriceBollingerChart;
